import { toFen } from '../game/fen';
import { moveToICCS } from '../game/notation';
import { legalMoves } from '../game/rules';
import type { BrainDecision, ModelDefinition, RemoteBrainRequest } from './types';

interface GatewayResponse {
  move?: unknown;
  reason?: unknown;
  confidence?: unknown;
  latencyMs?: unknown;
  error?: unknown;
}

export class RemoteBrainError extends Error {
  constructor(readonly code: string, message: string) {
    super(message);
    this.name = 'RemoteBrainError';
  }
}

const REMOTE_FAILURE_MESSAGES: Readonly<Record<string, string>> = {
  MISSING_KEY: '尚未配置模型密钥。',
  UPSTREAM_AUTH_FAILED: '模型密钥无效或无权使用该模型。',
  UPSTREAM_RATE_LIMIT: '模型请求过于频繁，请稍后再试。',
  UPSTREAM_TIMEOUT: '模型思考超时。',
  UPSTREAM_REQUEST_REJECTED: '模型服务拒绝了请求，请检查模型名称与参数支持情况。',
  UPSTREAM_SERVER_ERROR: '模型厂商服务异常，请稍后重试。',
  UPSTREAM_NETWORK_ERROR: '服务器无法连接模型厂商。',
  UPSTREAM_BAD_RESPONSE: '模型厂商返回了无法识别的数据。',
  UPSTREAM_FAILED: '模型厂商未能处理本次请求。',
  INVALID_REQUEST: '本地模型网关拒绝了请求数据。',
  INVALID_KEY_FORMAT: 'API Key 格式异常，请只填写密钥本身。',
  INVALID_MODEL_MOVE: '模型没有返回合法着法。',
  INVALID_MOVE: '模型返回的着法未通过本地规则校验。',
  BAD_GATEWAY_RESPONSE: '模型网关返回了无法识别的数据。',
  RATE_LIMITED: '本地模型网关请求过于频繁。',
};

export function remoteBrainFailureCode(error: unknown): string {
  if (error instanceof RemoteBrainError) return error.code;
  if (error instanceof DOMException && error.name === 'AbortError') return 'REQUEST_ABORTED';
  if (error instanceof TypeError) return 'GATEWAY_UNREACHABLE';
  return 'UNKNOWN';
}

export function gatewayClientFailureCause(error: unknown): string {
  if (!(error instanceof TypeError)) return 'OTHER';
  const message = error.message.toLowerCase();
  if (message.includes('header') || message.includes('bytestring') || message.includes('iso-8859')) {
    return 'HEADER_REJECTED';
  }
  if (message.includes('failed to fetch') || message.includes('networkerror')) return 'FETCH_BLOCKED';
  if (message.includes('url')) return 'INVALID_URL';
  return 'TYPE_ERROR';
}

export function describeRemoteBrainFailure(error: unknown): string {
  if (error instanceof RemoteBrainError) {
    return REMOTE_FAILURE_MESSAGES[error.code] ?? '模型服务暂时不可用。';
  }
  if (error instanceof DOMException && error.name === 'AbortError') return '模型请求被中止。';
  if (error instanceof TypeError) return '浏览器无法连接本地模型网关。';
  return '模型服务暂时不可用。';
}

export class RemoteModelBrain {
  constructor(
    readonly model: ModelDefinition,
    private readonly fetchImpl: typeof fetch = fetch,
  ) {}

  async chooseMove(request: RemoteBrainRequest): Promise<BrainDecision> {
    const apiKey = request.apiKey
      .trim()
      .replace(/^Bearer\s+/i, '')
      .replace(/\s+/g, '');
    if (!apiKey) throw new RemoteBrainError('MISSING_KEY', '缺少模型密钥');
    if (!/^[\x21-\x7e]+$/.test(apiKey)) {
      throw new RemoteBrainError('INVALID_KEY_FORMAT', '密钥格式错误');
    }

    // Native browser functions must not receive RemoteModelBrain as their `this` value.
    const fetchImpl = this.fetchImpl;
    const response = await fetchImpl('/api/model-move', {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-provider-api-key': apiKey,
      },
      body: JSON.stringify({
        provider: this.model.provider,
        model: this.model.model,
        fen: toFen(request.state),
        recentMoves: request.recentMoves.slice(-16),
      }),
      signal: request.signal,
    });

    let payload: GatewayResponse = {};
    try {
      payload = await response.json() as GatewayResponse;
    } catch {
      throw new RemoteBrainError('BAD_GATEWAY_RESPONSE', '模型网关返回异常');
    }
    if (!response.ok) {
      throw new RemoteBrainError(
        typeof payload.error === 'string' ? payload.error : 'REMOTE_FAILED',
        '模型调用失败',
      );
    }

    const legal = legalMoves(request.state);
    const move = legal.find(candidate => moveToICCS(candidate) === payload.move);
    if (!move) throw new RemoteBrainError('INVALID_MOVE', '模型未返回合法着法');

    return {
      move,
      reason: typeof payload.reason === 'string' ? payload.reason.slice(0, 240) : undefined,
      confidence: typeof payload.confidence === 'number' ? payload.confidence : undefined,
      latencyMs: typeof payload.latencyMs === 'number' ? payload.latencyMs : 0,
    };
  }
}
